/**
 * Authentication API endpoints
 *
 * This module contains all API endpoint functions related to authentication.
 */

import { apiClient } from './client';

/**
 * Login request payload
 */
export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

/**
 * Login response - matches backend LoginUseCase output
 */
export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    name: string;
    phone: string | null;
    avatarUrl: string | null;
    emailVerified: boolean;
    roles: string[];
  };
}

/**
 * Register request payload
 */
export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  organizationName?: string;
}

/**
 * Register response - matches backend RegisterUseCase output
 */
export interface RegisterResponse {
  id: string;
  email: string;
  name: string;
  phone: string | null;
}

/**
 * Refresh token response - matches backend RefreshTokenUseCase output
 */
export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
}

/**
 * Password reset request
 */
export interface PasswordResetRequest {
  email: string;
}

/**
 * Password reset confirmation
 */
export interface PasswordResetConfirmRequest {
  token: string;
  password: string;
}

/** Login request timeout (ms) - avoid infinite "Signing in..." when API is unreachable */
const LOGIN_TIMEOUT_MS = 15_000;

/**
 * Login with email and password
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), LOGIN_TIMEOUT_MS);
  try {
    const result = await apiClient.post<LoginResponse>('/api/auth/login', credentials, {
      signal: controller.signal,
    });
    return result;
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('Connection timed out. Make sure the API server is running (e.g. pnpm dev:all).');
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Register a new user
 */
export async function register(data: RegisterRequest): Promise<RegisterResponse> {
  return apiClient.post<RegisterResponse>('/api/auth/register', data);
}

/**
 * Logout current user
 */
export async function logout(refreshToken: string): Promise<void> {
  return apiClient.post<void>('/api/auth/logout', { refreshToken });
}

/**
 * Refresh authentication token
 */
export async function refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
  return apiClient.post<RefreshTokenResponse>('/api/auth/refresh', { refreshToken });
}

/**
 * Request password reset email
 */
export async function requestPasswordReset(email: string): Promise<void> {
  return apiClient.post<void>('/api/auth/password-reset/request', { email });
}

/**
 * Confirm password reset with token
 */
export async function confirmPasswordReset(
  data: PasswordResetConfirmRequest
): Promise<void> {
  return apiClient.post<void>('/api/auth/password-reset/confirm', data);
}

/**
 * Get current user profile
 */
export async function getCurrentUser(): Promise<LoginResponse['user']> {
  return apiClient.get<LoginResponse['user']>('/api/auth/me');
}

/**
 * Update password
 */
export interface UpdatePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export async function updatePassword(data: UpdatePasswordRequest): Promise<void> {
  return apiClient.post<void>('/api/auth/update-password', data);
}
